export const languages = [
  { code: "en", label: "English" },
  { code: "zh", label: "中文" },
  { code: "es", label: "Español" },
  { code: "ru", label: "Русский" },
  { code: "tg", label: "Тоҷикӣ" }
] as const;

export type Lang = (typeof languages)[number]["code"];
export const defaultLang: Lang = "en";

const en = {
  announcement: "Free worldwide support · Manual payment accepted · Multilingual shopping",
  shop: "Shop",
  categories: "Categories",
  newArrivals: "New Arrivals",
  journal: "Journal",
  support: "Support",
  search: "Search products",
  heroTag: "New Season Collection",
  heroTitle: "Smart Products for Everyday Living",
  heroSubtitle: "Shop practical lifestyle essentials with multilingual support, manual payment options, and a simple checkout experience.",
  shopNow: "Shop Now",
  exploreCategories: "Explore Categories",
  securePaymentReview: "Secure Payment Review",
  globalCatalog: "Global Catalog",
  fastSupport: "Fast Support",
  shopByCategory: "Shop by Category",
  dailyEssentials: "Find your daily essentials",
  viewAll: "View all",
  electronics: "Electronics",
  lifestyle: "Lifestyle",
  workspace: "Workspace",
  travel: "Travel",
  productsCount: "products",
  featuredProducts: "Featured Products",
  addToCart: "Add to Cart",
  viewDetails: "View Details",
  bestSellers: "Best Sellers",
  limitedDeals: "Limited Deals for Smart Buyers",
  limitedDealsText: "Discover selected products with clear pricing and simple manual payment review.",
  viewDeals: "View Deals",
  whyChooseUs: "Why Choose Us",
  confidentBuying: "Built for confident buying",
  multilingualShopping: "Multilingual Shopping",
  buyerSupport: "Buyer Support",
  secureManualPayment: "Secure Manual Payment",
  globalProductCatalog: "Global Product Catalog",
  confidentBuyingText1: "Submit TXID and remarks for a clear payment review.",
  confidentBuyingText2: "Browse core pages in five languages.",
  confidentBuyingText3: "Launch products quickly with image URLs and CSV import.",
  confidentBuyingText4: "Support flows are ready for pre-sale and after-sale help.",
  customerFavorites: "Customer favorites",
  newsletterTitle: "Get product drops and payment updates",
  newsletterText: "Join the list for new arrivals, seasonal collections, and buyer support updates.",
  emailAddress: "Email address",
  subscribe: "Subscribe",
  privacyPolicy: "Privacy Policy",
  termsOfService: "Terms of Service",
  refundPolicy: "Refund Policy",
  shippingPolicy: "Shipping Policy",
  helpCenter: "Help Center",
  contact: "Contact",
  compare: "Compare",
  account: "Account",
  wishlist: "Wishlist",
  cart: "Cart",
  checkout: "Checkout",
  orderSuccess: "Order Success",
  paymentInstructions: "Payment Instructions",
  submitPaymentInfo: "Submit Payment Info",
  admin: "Admin"
};

const zh: typeof en = {
  announcement: "全球支持 · 支持手动付款 · 多语言购物",
  shop: "商店",
  categories: "分类",
  newArrivals: "新品",
  journal: "资讯",
  support: "支持",
  search: "搜索商品",
  heroTag: "新季精选",
  heroTitle: "精选实用好物，轻松全球购物",
  heroSubtitle: "支持多语言浏览、手动付款、订单审核和便捷结账，为跨境购物提供更清晰的体验。",
  shopNow: "立即选购",
  exploreCategories: "查看分类",
  securePaymentReview: "安全付款审核",
  globalCatalog: "全球商品目录",
  fastSupport: "快速支持",
  shopByCategory: "按分类选购",
  dailyEssentials: "发现你的日常好物",
  viewAll: "查看全部",
  electronics: "电子产品",
  lifestyle: "生活用品",
  workspace: "办公好物",
  travel: "旅行装备",
  productsCount: "件商品",
  featuredProducts: "精选商品",
  addToCart: "加入购物车",
  viewDetails: "查看详情",
  bestSellers: "热卖商品",
  limitedDeals: "聪明买家的限时优惠",
  limitedDealsText: "发现价格清晰、支持手动付款审核的精选商品。",
  viewDeals: "查看优惠",
  whyChooseUs: "为什么选择我们",
  confidentBuying: "为安心购物而设计",
  multilingualShopping: "多语言购物",
  buyerSupport: "买家支持",
  secureManualPayment: "安全手动付款",
  globalProductCatalog: "全球商品目录",
  confidentBuyingText1: "提交 TXID 和备注，付款审核更清晰。",
  confidentBuyingText2: "核心页面支持五种语言浏览。",
  confidentBuyingText3: "通过图片 URL 和 CSV 快速上架商品。",
  confidentBuyingText4: "售前和售后支持流程已经准备就绪。",
  customerFavorites: "客户喜爱的商品",
  newsletterTitle: "获取新品上架和付款更新",
  newsletterText: "订阅新品、季节精选和买家支持更新。",
  emailAddress: "邮箱地址",
  subscribe: "订阅",
  privacyPolicy: "隐私政策",
  termsOfService: "服务条款",
  refundPolicy: "退款政策",
  shippingPolicy: "物流政策",
  helpCenter: "帮助中心",
  contact: "联系我们",
  compare: "对比",
  account: "账户",
  wishlist: "收藏",
  cart: "购物车",
  checkout: "去结算",
  orderSuccess: "下单成功",
  paymentInstructions: "付款说明",
  submitPaymentInfo: "提交付款信息",
  admin: "后台"
};

const dict: Record<Lang, Record<string, string>> = { en, zh, es: en, ru: en, tg: en };

export function normalizeLang(value?: string): Lang {
  return languages.some((lang) => lang.code === value) ? (value as Lang) : defaultLang;
}

export function t(lang: string | undefined, key: keyof typeof en | string) {
  const code = normalizeLang(lang);
  return dict[code][key] ?? dict.en[key] ?? key;
}

export function switchLangPath(pathname: string, nextLang: Lang) {
  const parts = pathname.split("/").filter(Boolean);
  if (languages.some((lang) => lang.code === parts[0])) parts[0] = nextLang;
  else parts.unshift(nextLang);
  return `/${parts.join("/")}`;
}
